// LR1-12 ROUND FOUR + THE HEADER-PAIR AMENDMENT — THE BELL AND THE MAIL, IN THE TOP MENU.
//
// Her ruling: both sit between Site Instructions and Member, on every page and on both viewports,
// each with its own unread count. This is THE bell and THE inbox. The Living Room's local
// placement is retired, and the room keeps only its own content: the feed, Shared Spells, the share
// button and search.
//
// **THE TIER IS THE ROOM'S.** Messages and notifications are Living Room surfaces, so a below-tier
// tap raises the warm box rather than opening an empty panel — and it is the SERVER's refusal that
// raises it (the 402 carries the box's payload), never a guess made here, which is the same fix the
// room itself needed after the deep-link flicker.
//
// **L2.7 — AND FOR A PAID MEMBER BELOW THAT TIER IT RAISED NOTHING AT ALL, WHICH IS HER LIVE
// FINDING.** `guard` handed `onRequireMembership` an EMPTY ACTION, and that function's first line
// is *"if she is a member, run the action"* — so a paid Newbie's tap on either icon called
// `() => {}` and the site did not move. The decision is `communityDoorAnswer`'s now (it has no
// silent branch), and what is remembered is the server's own 402 PAYLOAD rather than a boolean, so
// the box that opens names the door she touched.
import { useCallback, useEffect, useState } from "react";
import { sharedRoomRead } from "../lib/room-me";
import { HeaderIconButton } from "./HeaderIconButton";
import { NotificationsPanel } from "./LivingRoomInbox";
import { PAGE_PATHS, pageFromPath } from "../lib/pages";
import { inboxFromState } from "../lib/inbox-return";
import { json, type Me, type Notification } from "./LivingRoomShared";
import { roomItems, communityDoorAnswer } from "../livingRoom.js";
import { useRefresh } from "./PullToRefresh";
// 14-B5: the bell and the mail are on EVERY page, and their whole text is an accessible name — the
// half of this chrome nobody looking at the screen can check. HI1 moved the composition of that name
// into `HeaderIconButton`, where a gate CALLS it once per language and per unread state.
import { DEFAULT_LANGUAGE } from "../language.js";

export function HouseInbox({
  isMember, onRequireMembership, language = DEFAULT_LANGUAGE,
}: {
  isMember: boolean;
  // L2.7: the SECOND argument is what makes the box open for a member — `requireMembership` runs the
  // action unless it is handed a lock. It has always taken one (T8-3); this prop's type had not.
  onRequireMembership: (action: () => void, lock?: Record<string, unknown>) => void;
  language?: string;
}) {
  const [me, setMe] = useState<Me | null>(null);
  const [unreadMail, setUnreadMail] = useState(0);
  const [open, setOpen] = useState<"" | "notes">("");
  // THE SERVER'S OWN REFUSAL, KEPT WHOLE. A boolean here is what left the header able to say only
  // *"something was refused"*, which is not enough to draw the box she is owed.
  const [lock, setLock] = useState<Record<string, unknown> | null>(null);

  // ITEM 5 — **THE MAIL BUTTON NAVIGATES NOW; IT DOES NOT RAISE A PANEL.** The inbox is a screen of
  // its own (her order), so this goes through the house's own routing rather than mounting a second
  // copy of it in the header — and it uses `PAGE_PATHS`, never a typed "/inbox", because R26 paid
  // for the one navigation on this site that went around the route map.
  // ITEM 1 — **THE PUSH CARRIES A STAMP SAYING WHERE SHE WAS**, because that is the only thing that
  // can honestly tell "we put this entry on the stack" from "she arrived by a push notification, a
  // pasted link or a cold load". `history.length` counts other origins' entries too and
  // `document.referrer` is empty on a same-document push, so neither can answer it — and getting it
  // wrong is the one failure that walks her out of 12th House entirely (see `src/lib/inbox-return.ts`).
  const goToInbox = (threadId?: string | null) => {
    const url = threadId ? `${PAGE_PATHS.inbox}?thread=${encodeURIComponent(threadId)}` : PAGE_PATHS.inbox;
    window.history.pushState(inboxFromState(pageFromPath(window.location.pathname)), "", url);
    window.dispatchEvent(new PopStateEvent("popstate"));
    setOpen("");
  };

  // ONE POLL FOR BOTH COUNTS, and it is deliberately unhurried: two badges in a header do not need
  // to be live to the second, and a header that polls hard is a header that costs on every page of
  // the site at once.
  const refresh = useCallback(async () => {
    const d = await sharedRoomRead("/api/living-room/me", json);
    if (d?.locked) { setLock(d as Record<string, unknown>); return; }
    if (!d?.ok) return;
    setLock(null);
    setMe(d);
    const inbox = await json("/api/living-room/inbox");
    // R84 — **A FORGED EMPTY LIST TOOK THE DOT OFF THE MAIL BUTTON**, on a poll that runs every 90
    // seconds on every page of this site: one dropped read and the badge said she had no unread
    // messages. The count is a fact about her inbox, so a read that did not land leaves the last one
    // she was actually told standing rather than replacing it with a zero (R81: a count is the same
    // fact one size down).
    const threads = roomItems(inbox, "threads");
    if (threads) setUnreadMail(threads.reduce((n: number, t: { unread: number }) => n + (t.unread || 0), 0));
  }, []);

  useEffect(() => {
    if (!isMember) return;
    refresh();
    const t = window.setInterval(refresh, 90_000);
    return () => window.clearInterval(t);
  }, [isMember, refresh]);

  // LR1 ROUND FIVE: tapping an @name anywhere in the room asks the header to open that conversation.
  // An event rather than a prop, because the room and this header are siblings in App and the room
  // must not have to know the header exists.
  useEffect(() => {
    const onOpenRequest = (e: Event) => {
      goToInbox((e as CustomEvent<{ threadId?: string }>).detail?.threadId || null);
    };
    window.addEventListener("house-inbox:open", onOpenRequest);
    return () => window.removeEventListener("house-inbox:open", onOpenRequest);
  }, []);

  // LR12-2 — A MESSAGE PUSH LANDS IN THE THREAD, AND THIS IS THE HALF THAT HAD NOWHERE TO LAND.
  // The bell can open the inbox panel in place because it is already on the page; a push has nothing
  // but a URL. `livingRoomUrl` sends a DM to `/living-room?thread=…`, and the inbox opens itself on
  // arrival here — in the header, because the header is what owns the inbox on every page, so this
  // works whether or not the room itself ever mounts.
  useEffect(() => {
    if (!isMember) return;
    // LR12-2's push URL is `/living-room?thread=…` and it STAYS that, because a URL already out on
    // somebody's lock screen is not ours to change. It lands on the inbox screen now instead of
    // raising a panel over the room.
    if (window.location.pathname === PAGE_PATHS.inbox) return;
    const wanted = new URLSearchParams(window.location.search).get("thread");
    if (!wanted) return;
    goToInbox(wanted);
  }, [isMember]);

  // P2-7: the bell and the mail counts are on EVERY page, and they poll on a deliberately unhurried
  // 90 second timer — which is exactly the staleness a pull exists to answer. Joining here means the
  // counts are fresh after a pull no matter which room she was standing in.
  useRefresh("house-inbox", refresh);

  // A below-tier tap gets the box, and a tap NEVER gets nothing (L2.7). The answer is the shared
  // law's, so the header, the inbox screen and the room cannot drift apart about what a refused door
  // does — and the lock it carries is the server's own, so the header still never guesses at
  // entitlement, it just repeats what it was told.
  const guard = (then: () => void) => {
    const answer = communityDoorAnswer({ isMember, lock });
    if (answer.kind === "box") { onRequireMembership(() => {}, answer.lock); return; }
    then();
  };

  // LR1-10: a notification lands EXACTLY on its target. A thread opens the inbox at that thread; a
  // post navigates to the room with the post named, which the room reads on mount.
  const goToTarget = (t: Notification["target"]) => {
    if (t.kind === "thread") { goToInbox(t.id); return; }
    if (t.kind === "post") {
      // LR12-2: THE URL IS THE TARGET'S OWN, not a second copy typed here. This line used to build
      // `?post=&comment=` by hand while the push carrier read `target.url` and got nothing, so the
      // two carriers of one law pointed at different places — the bell at the comment, the lock
      // screen at the home page. `livingRoomUrl` is the one answer and the server sends it down.
      window.history.pushState({}, "", t.url || `/living-room?post=${encodeURIComponent(t.id)}`);
      window.dispatchEvent(new PopStateEvent("popstate"));
      setOpen("");
    }
  };

  const unreadNotes = me?.unread || 0;

  return (
    <div className="house-inbox">
      {/* HI1 — both buttons are ONE component rendered twice. The size, the glow and the badge text
          are the shared law's (`src/headerIcons.js`); what is decided here is what a tap DOES, which
          is the only thing that really differs between them. */}
      <HeaderIconButton kind="notifications" unread={unreadNotes} language={language}
        onClick={() => guard(() => setOpen("notes"))} />
      <HeaderIconButton kind="messages" unread={unreadMail} language={language}
        onClick={() => guard(() => goToInbox(null))} />

      {open === "notes" ? <NotificationsPanel onClose={() => { setOpen(""); refresh(); }} onGo={goToTarget} /> : null}
    </div>
  );
}
